import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import type { OrderDrawerIdentifier } from "../types";
import { useGetOrderApprovalHistory } from "@features/orders-features/hooks/useGetOrderApprovalHistory";
import type { OrderRow } from "@features/orders-features/types/types";
import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { AppendIfPrefixExists } from "../../../../../../../modules/sitePaths";
import { getShiComBaseUrl } from "@lib/shiComBaseUrl";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import { useOrderTelemetrySection } from "../orderTelemetrySection";
import { getApprovalReviewHref } from "@features/orders-features/components/order-approval-drawer/approvalReviewRoute";

interface OrderDrawerApprovalHistoryProps {
  identifier: OrderDrawerIdentifier;
  enabled: boolean;
  order: OrderRow;
  disableGutters?: boolean;
}

const APPROVAL_STATUSES = new Set([
  "approval pending",
  "awaiting approval",
  "awaiting transfer",
  "transfer approver",
]);

function isPendingApprovalStatus(status: string | undefined): boolean {
  return APPROVAL_STATUSES.has((status ?? "").toLowerCase());
}

function parseOrderDate(value: string | undefined): DateTime | null {
  if (!value) {
    return null;
  }

  const local = DateTime.fromISO(value, { zone: "local" });
  if (local.isValid) {
    return local;
  }

  const zoned = DateTime.fromISO(value, { setZone: true });
  if (zoned.isValid) {
    return zoned.toLocal();
  }

  return null;
}

function getPendingDays(placedDate: DateTime | null): number {
  if (!placedDate || !placedDate.isValid) {
    return 0;
  }

  const placedDay = placedDate.startOf("day");
  const today = DateTime.now().startOf("day");
  const diffDays = Math.floor(today.diff(placedDay, "days").days);

  return Number.isFinite(diffDays) ? Math.max(0, diffDays) : 0;
}

function formatStepDate(value: string | undefined): string {
  if (!value) {
    return "";
  }

  const local = DateTime.fromISO(value, { zone: "local" });
  if (local.isValid) {
    return local.toFormat("M/d/yyyy");
  }

  const zoned = DateTime.fromISO(value, { setZone: true });
  if (zoned.isValid) {
    return zoned.toLocal().toFormat("M/d/yyyy");
  }

  return "";
}

function isPendingStepStatus(status: string | undefined, statusCode: string | undefined): boolean {
  const normalizedStatus = (status ?? "").trim().toLowerCase();
  const normalizedCode = (statusCode ?? "").trim().toLowerCase();

  return normalizedStatus === "pending"
    || normalizedStatus === "approval pending"
    || normalizedStatus === "awaiting approval"
    || normalizedStatus === "transfer approver"
    || normalizedCode === "p"
    || normalizedCode === "pending"
    || normalizedCode === "approval pending"
    || normalizedCode === "awaiting approval"
    || normalizedCode === "t"
    || normalizedCode === "transfer approver";
}

function PendingSummaryIcon({ canReview }: { canReview: boolean }) {
  return (
    <HowToRegIcon
      sx={{
        fontSize: 18,
        color: canReview ? "error.main" : "action.active",
      }}
    />
  );
}

function StepStatusIcon({ status }: { status?: string }) {
  const normalized = (status ?? "").toLowerCase();

  if (normalized === "approved") {
    return <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />;
  }

  if (normalized === "denied") {
    return <BlockIcon color="error" sx={{ fontSize: 18 }} />;
  }

  return <HourglassTopIcon color="action" sx={{ fontSize: 18 }} />;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 0.75,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
        {label}
      </Typography>

      <Typography variant="body2" sx={{ textAlign: "right", wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}

function ApprovalCard({
  children,
  footer,
}: Readonly<{
  children: React.ReactNode;
  footer?: React.ReactNode;
}>) {
  return (
    <Box
      sx={{
        borderRadius: 1,
        bgcolor: "action.hover",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.5, py: 1 }}>{children}</Box>
      {footer ? (
        <>
          <Divider />
          <Box sx={{ px: 1.5, py: 0.75 }}>{footer}</Box>
        </>
      ) : null}
    </Box>
  );
}

export function OrderDrawerApprovalHistory({
  identifier,
  enabled,
  order,
  disableGutters = false,
}: Readonly<OrderDrawerApprovalHistoryProps>) {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();
  const {
    enabled: orderApprovalsEnabled,
    loading: orderApprovalsLoading,
  } = useFeatureFlag("nomads.order-approvals", false);
  const telemetrySection = useOrderTelemetrySection();

  const { approvalHistory } = useGetOrderApprovalHistory({
    identifier,
    enabled,
  });

  const webOrderId = identifier.webOrderId ?? 0;
  const biOrderId = identifier.biOrderId ?? 0;
  const isWebOrder = webOrderId > 0 && biOrderId <= 0;
  const pendingSteps = useMemo(
    () => approvalHistory.steps.filter((step) => isPendingStepStatus(step.status, step.statusCode)),
    [approvalHistory.steps],
  );
  const isPending = isPendingApprovalStatus(order.status);
  const hasPendingApproval = isPending || pendingSteps.length > 0 || approvalHistory.canReview;
  const showSection = isWebOrder && (approvalHistory.hasApprovalWorkflow || hasPendingApproval);
  const pendingFromDate = useMemo(() => {
    const pendingStepDates = pendingSteps
      .map((step) => parseOrderDate(step.date))
      .filter((date): date is DateTime => Boolean(date?.isValid))
      .sort((a, b) => a.toMillis() - b.toMillis());

    if (pendingStepDates.length > 0) {
      return pendingStepDates[0];
    }

    return parseOrderDate(order.orderDate);
  }, [order.orderDate, pendingSteps]);
  const pendingDays = useMemo(() => getPendingDays(pendingFromDate), [pendingFromDate]);
  const pendingApprovers = useMemo(() => {
    if (approvalHistory.pendingApprovers.length > 0) {
      return approvalHistory.pendingApprovers;
    }

    return [...new Set(pendingSteps.map((step) => step.approver?.trim()).filter(Boolean))];
  }, [approvalHistory.pendingApprovers, pendingSteps]);
  const completedSteps = useMemo(
    () => approvalHistory.steps.filter((step) => !isPendingStepStatus(step.status, step.statusCode)),
    [approvalHistory.steps],
  );
  const isApproverView = hasPendingApproval && approvalHistory.canReview;
  const pendingStatusLabel = isApproverView ? "Approval Pending" : "Awaiting Approval";

  if (!showSection) {
    return null;
  }

  const reviewOrderUrl =
    orderApprovalsEnabled && !orderApprovalsLoading
      ? AppendIfPrefixExists(getApprovalReviewHref(webOrderId))
      : `${getShiComBaseUrl()}/Checkout/WebOrderapprove.aspx?weborderID=${webOrderId}`;
  const showReviewButton = isApproverView;

  return (
    <Card elevation={1} sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        onClick={() => setExpanded((value) => !value)}
        aria-expanded={expanded}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ textTransform: "uppercase", fontWeight: "normal" }}>
          Approval History
        </Typography>
        <ExpandMoreIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
        />
      </CardActionArea>

      <Collapse in={expanded}>
        {hasPendingApproval ? (
          <>
            <Box sx={{ px: 2, py: 1.5 }}>
              <ApprovalCard
                footer={showReviewButton ? (
                  <Button
                    variant="text"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (orderApprovalsEnabled && !orderApprovalsLoading) {
                        navigate(reviewOrderUrl);
                      } else {
                        window.location.assign(reviewOrderUrl);
                      }
                    }}
                    sx={{ px: 0, fontWeight: 700 }}
                    {...telemetryProps({
                      name: "orders_review_order",
                      section: telemetrySection,
                      label: "Review order",
                      ...(webOrderId ? { id: String(webOrderId) } : {}),
                    })}
                  >
                    Review Order
                  </Button>
                ) : undefined}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <PendingSummaryIcon canReview={isApproverView} />
                  <Typography variant="body2" fontWeight={500}>
                    {pendingStatusLabel}
                  </Typography>
                  {isApproverView && (
                    <Typography variant="body2" sx={{ color: "warning.main" }}>
                      {pendingDays === 0 ? "Pending today" : `${pendingDays} days pending`}
                    </Typography>
                  )}
                </Box>

                <DetailRow label="Ordered by" value={order.orderedBy || "-"} />

                {pendingApprovers.length > 0 && (
                  <DetailRow label="Approver" value={pendingApprovers.join(", ")} />
                )}
              </ApprovalCard>
            </Box>
          </>
        ) : (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.75 }}>
            <DetailRow label="Ordered by" value={order.orderedBy || "-"} />
          </Box>
        )}

        {completedSteps.length > 0 && (
          <Box sx={{ p: 2, pt: 1.5 }}>
            {completedSteps.map((step, index) => {
              const stepDate = formatStepDate(step.date);

              return (
                <ApprovalCard
                  key={`${step.statusCode ?? "status"}-${index}`}
                  footer={undefined}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <StepStatusIcon status={step.status} />
                      <Typography variant="body2" fontWeight={600}>
                        {step.status || "Pending"}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {stepDate}
                    </Typography>
                  </Box>

                  <DetailRow
                    label={isPendingStepStatus(step.status, step.statusCode) ? "Approver" : "Approved by"}
                    value={step.approver || "-"}
                  />

                  {step.comments && <DetailRow label="Comments" value={step.comments} />}
                </ApprovalCard>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Card>
  );
}
